import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getOnboardingPath, OnboardingPages, Pages } from '../../../common/src/pages.ts';
import { deriveTrialState } from '../../../common/src/trial_state.ts';
import BackArrowIcon from '../assets/arrow-previous-left-icon.svg?react';
import CelebrationIcon from '../assets/celebration.svg?react';
import CheckMarkIcon from '../assets/check-mark-icon.svg?react';
import QuarterNoteIcon from '../assets/quarter-note.svg?react';
import { localStorageGet, LocalStorageKey } from '../data/client_local_storage.ts';
import { synchronizeHouseholdWithServer, useHousehold } from '../data/use_household.tsx';
import { goBack } from '../util/go_back.ts';
import { getUrlForPage } from '../util/path.ts';
import { consumeSubscriptionReturnOrigin } from '../util/subscription_flow_origin.ts';
import { useCurrentDate } from '../util/use_current_date.tsx';

type TrialFlowStep = 'gate' | 'confirm';
interface SubscriptionTrialLocationState {
  songTitle?: string;
}

export function SubscriptionTrialPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<TrialFlowStep>('gate');
  const routeState = location.state as SubscriptionTrialLocationState | undefined;

  const onBack = useCallback(async () => {
    if (step === 'confirm') {
      setStep('gate');
      return;
    }
    await goBack(navigate);
  }, [navigate, step]);

  return (
    <FullScreen>
      <GateHeader>
        <BackButton aria-label="Back" onClick={onBack}>
          <BackArrow />
        </BackButton>
        {step === 'gate' && routeState?.songTitle && <HeaderSongTitle>{routeState.songTitle}</HeaderSongTitle>}
      </GateHeader>
      {step === 'gate' ? <SubscriptionGate onTryTrial={() => setStep('confirm')} /> : <TrialStartConfirmation />}
    </FullScreen>
  );
}

function SubscriptionGate({ onTryTrial }: { onTryTrial: () => void }) {
  const navigate = useNavigate();
  const { household } = useHousehold();
  const clientDate = useCurrentDate();
  const trialState = deriveTrialState({ trialStartDate: household?.trialStartDate, clientDate });
  const showTrialCta = trialState.status === 'pre-trial';

  const onSubscribe = useCallback(async () => {
    await navigate(getOnboardingPath(OnboardingPages.SubscribeStart));
  }, [navigate]);

  return (
    <CenteredPanel>
      <MutedIconCircle>
        <GateIcon aria-hidden="true" />
      </MutedIconCircle>
      <GateHeadline>
        Hear every part.
        <br />
        Sing with confidence.
      </GateHeadline>
      <BodyText>
        Subscribe to access sheet music and audio for all four voice parts across your church&apos;s hymnal.
      </BodyText>
      <ButtonStack>
        {showTrialCta && <PrimaryButton onClick={onTryTrial}>Try 7 days free</PrimaryButton>}
        {showTrialCta ? (
          <SecondaryButton onClick={onSubscribe}>Subscribe now</SecondaryButton>
        ) : (
          <PrimaryButton onClick={onSubscribe}>Subscribe now</PrimaryButton>
        )}
      </ButtonStack>
      <FooterCopy>Starting at $4.99/mo. Covers your whole household.</FooterCopy>
    </CenteredPanel>
  );
}

function TrialStartConfirmation() {
  const navigate = useNavigate();
  const { household, setHousehold } = useHousehold();
  const clientDate = useCurrentDate();
  const [isStarting, setIsStarting] = useState(false);
  const householdName = getTrialHouseholdName(household?.familyLastName);

  const onStartTrial = useCallback(async () => {
    if (isStarting) {
      return;
    }
    setIsStarting(true);
    try {
      await synchronizeHouseholdWithServer({ force: true });
      const refreshedHousehold = localStorageGet(LocalStorageKey.Household);
      if (!refreshedHousehold) {
        // The server no longer has this household: the demo redirect (useDisplayDemoPage) takes over
        return;
      }
      if (refreshedHousehold.trialStartDate) {
        await navigateAfterStoredTrial(refreshedHousehold.trialStartDate, clientDate, navigate);
        return;
      }
      await setHousehold((currentHousehold) =>
        currentHousehold && !currentHousehold.trialStartDate
          ? { ...currentHousehold, trialStartDate: clientDate }
          : currentHousehold,
      );
      await synchronizeHouseholdWithServer({ force: true });
      const updatedHousehold = localStorageGet(LocalStorageKey.Household);
      if (updatedHousehold?.trialStartDate) {
        await navigateAfterStoredTrial(updatedHousehold.trialStartDate, clientDate, navigate);
        return;
      }
      await navigate(getUrlForPage(Pages.Today));
    } finally {
      setIsStarting(false);
    }
  }, [clientDate, isStarting, navigate, setHousehold]);

  return (
    <CenteredPanel>
      <PurpleIconCircle>
        <ConfirmIcon aria-hidden="true" />
      </PurpleIconCircle>
      <Headline>7 days free for your household</Headline>
      <BodyText>
        Everyone in {householdName} gets full access to all music, sheet music, and audio parts. No credit card
        required.
      </BodyText>
      <FeatureList>
        {trialFeatureList.map((feature) => (
          <FeatureItem key={feature}>
            <FeatureCheck aria-hidden="true" />
            <span>{feature}</span>
          </FeatureItem>
        ))}
      </FeatureList>
      <PrimaryButton onClick={onStartTrial} disabled={isStarting}>
        {isStarting ? 'Starting...' : 'Start free trial'}
      </PrimaryButton>
      <FooterCopy>No credit card. Trial ends automatically after 7 days.</FooterCopy>
    </CenteredPanel>
  );
}

async function navigateAfterStoredTrial(
  trialStartDate: string,
  clientDate: string,
  navigate: ReturnType<typeof useNavigate>,
) {
  const trialState = deriveTrialState({ trialStartDate, clientDate });
  if (trialState.status === 'expired') {
    await navigate(getOnboardingPath(OnboardingPages.SubscribeStart));
    return;
  }
  await navigate(getUrlForPage(consumeSubscriptionReturnOrigin()), { replace: true });
}

export function getTrialHouseholdName(familyLastName?: string): string {
  const trimmedName = familyLastName?.trim();
  return trimmedName ? `the ${trimmedName} household` : 'your household';
}

const trialFeatureList = [
  "All sheet music from your church's hymnal",
  'Audio for soprano, alto, tenor, and bass',
  'Full hymnal library',
  'Daily singing plans',
];

const FullScreen = styled.main`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 22px;
  background-color: var(--color-background);
  color: var(--color-text);
  overflow-y: auto;
`;

const GateHeader = styled.header`
  position: fixed;
  top: 18px;
  left: 18px;
  right: 18px;
  z-index: 1;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  align-items: center;
`;

const BackButton = styled.button`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
`;

const HeaderSongTitle = styled.div`
  grid-column: 2;
  min-width: 0;
  padding: 0 8px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.05rem;
  color: var(--color-text);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  text-wrap: nowrap;
`;

const BackArrow = styled(BackArrowIcon)`
  width: 28px;
  height: 28px;
  fill: var(--color-text-light);
`;

const CenteredPanel = styled.section`
  width: min(100%, 390px);
  min-height: min(560px, calc(var(--view-height) - 64px));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  text-align: center;
`;

const IconCircle = styled.div`
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
`;

const MutedIconCircle = styled(IconCircle)`
  background-color: var(--color-background-search);
`;

const PurpleIconCircle = styled(IconCircle)`
  background-color: #eeedfe;
`;

const GateIcon = styled(QuarterNoteIcon)`
  width: 28px;
  height: 48px;
  fill: var(--color-text-light);
`;

const ConfirmIcon = styled(CelebrationIcon)`
  width: 40px;
  height: 40px;
`;

const Headline = styled.h1`
  max-width: min(100%, 20ch);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 2rem;
  line-height: 1.08;
`;

const GateHeadline = styled(Headline)`
  font-size: 1.75rem;
`;

const BodyText = styled.p`
  max-width: 34ch;
  font-size: 1.08rem;
  line-height: 1.45;
  color: var(--color-text-darker);
`;

const ButtonStack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActionButton = styled.button`
  width: 100%;
  min-height: 48px;
  border-radius: 8px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.05rem;
  cursor: pointer;

  &:disabled {
    background-color: var(--color-background-button-disabled);
    cursor: default;
  }
`;

const PrimaryButton = styled(ActionButton)`
  color: var(--color-text-button-onboarding-primary);
  background-color: var(--color-primary-button);
  border: none;
`;

const SecondaryButton = styled(ActionButton)`
  color: var(--color-primary-button);
  background-color: var(--color-background);
  border: 2px solid var(--color-primary-button);
`;

const FooterCopy = styled.div`
  max-width: 34ch;
  font-size: 0.92rem;
  color: var(--color-text-light);
`;

const FeatureList = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
  list-style: none;
  text-align: left;
`;

const FeatureItem = styled.li`
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  color: var(--color-text);
`;

const FeatureCheck = styled(CheckMarkIcon)`
  width: 18px;
  height: 18px;
  fill: var(--color-primary-button);
`;
